import { useEffect, useState, Suspense, lazy } from 'react';
import { Alert, Box } from '@mui/material';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

import '@rhino/shared/assets/styles/App.scss';
import { sASCII_Orbotnik_Small } from '@rhino/shared/constants/global';
import { useShute } from '../shared/atoll/hooks/useShute';
import { initAtoll } from '../shared/atoll/init';
import ConnectionGate from '../shared/atoll/components/ConnectionGate';
import SsoLaunch from './SsoLaunch';

const Home = lazy(() => import('./home/Home'));
const NebulonSpread = lazy(() => import('./nebulon/Spread'));
const Cameras = lazy(() => import('./cameras/Cameras'));
const MediaArchive = lazy(() => import('./mediaviewer/MediaArchive'));

import { LoginDialog } from './header/LoginDialog'
import LogModal from '@rhino/shared/components/header/LogModal';
import ProtectedRoute from '../shared/atoll/components/ProtectedRoute';
import Waiting from '@rhino/shared/components/common/waiting/waiting';
// import SW from '@rhino/shared/services/pwa/SW';
import Header from './header/Header';
import { Footer } from '@rhino/shared/components/header/Footer';
import { selectOffline, setOffline } from '../shared/atoll/redux/shuteSlice';
import { SputnikAPIBaseURL } from '@rhino/shared/services/customShared';
import { useAppDispatch, useAppSelector } from '../shared/services/redux/hooks';
import DeveloperHeader from '@rhino/shared/components/header/DeveloperHeader';
import { refreshSession } from '../shared/atoll/atollAuth';
import { loginSuccess, setLoginOpen } from '../shared/atoll/redux/authSlice';

initAtoll({ baseURL: SputnikAPIBaseURL, atollBaseURL: '/atoll/', shuteURL: '/shute', clientId: 'orbotnik' });

function App() {

  useEffect(() => {
    console.log(sASCII_Orbotnik_Small)
    window.console = window.console || function(t: any) {};
    if (document.location.search.match(/type=embed/gi)) {
      window.parent.postMessage("resize", "*");
    };
  }, []);
  const dispatch = useAppDispatch();
  const isOffline = useAppSelector(selectOffline);
  const [sessionReady, setSessionReady] = useState(false);
  // Check if the server is available
  useEffect(() => {
    console.log(`trying ping ${SputnikAPIBaseURL}ping`)
    fetch(`${SputnikAPIBaseURL}ping`)
      .then((response) => response.ok)
      .then((available) => dispatch(setOffline(!available)))
      .catch(() => dispatch(setOffline(true))); // Handle fetch failure
  }, []); // This effect runs once after initial render

  // Restore session on load — an access token in sessionStorage may have
  // expired since the last visit; the refresh token (or atoll_rt cookie)
  // can still mint a fresh one. Mirrors apestaart's App.tsx bootstrap.
  useEffect(() => {
    if (window.location.pathname === '/atoll/launch') {
      // SsoLaunch owns the auth bootstrap on this route — running our own
      // refreshSession() here too would race it over the same one-time
      // renewal token and whichever call loses gets rejected.
      setSessionReady(true);
      return;
    }
    refreshSession(dispatch).then((result) => {
      if (result.success && result.data?.user) {
        dispatch(loginSuccess(result.data.user));
        dispatch(setLoginOpen(false));
      }
      setSessionReady(true);
    });
  }, []);

  useShute(isOffline);
  const environmentWarning = import.meta.env.VITE_ENVIRONMENT_WARNING;
  const tldr = `© 2021-${new Date().getFullYear()} UnbiasedRhino Productions, All rights reserved.`


  return (
    <>
      <DeveloperHeader environmentWarning={environmentWarning!}/>
      <Router>
        <Header />
        <Box>
          <ConnectionGate ready={sessionReady} message="Connecting to Orbotnik">
            <Suspense fallback={
              <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" sx={{height: "70vh"}}>
                <Alert severity="info">Loading Component</Alert>
                <Waiting className={'dot-floating'} />
              </Box>
            }>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/nebulon" element={<ProtectedRoute path="/nebulon"><NebulonSpread /></ProtectedRoute>} />
                <Route path="/cameras" element={<ProtectedRoute path="/cameras"><Cameras /></ProtectedRoute>} />
                <Route path="/mediaarchive" element={<ProtectedRoute path="/mediaarchive"><MediaArchive /></ProtectedRoute>} />
                <Route path="/atoll/launch" element={<SsoLaunch />} />
              </Routes>
              <LoginDialog />
              <LogModal />
            </Suspense>
          </ConnectionGate>
        </Box>
      </Router>
      <Footer tldr={tldr}/>
    </>
  );
}

export default App;
